import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import AppShell from "@/components/AppShell";
import AchievementsRow from "@/components/AchievementsRow";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Loader2, ArrowRight, ClipboardList, Map, MessageSquare, Compass, GraduationCap, Sparkles, TrendingUp } from "lucide-react";

const RIASEC_COLORS = {
  R: "#3B82F6", I: "#8B5CF6", A: "#FB7185", S: "#34D399", E: "#FBBF24", C: "#F97316",
};

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/dashboard/").then((r) => setData(r.data)).finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <AppShell>
        <div className="p-10 flex items-center gap-2 text-slate-300">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading your dashboard…
        </div>
      </AppShell>
    );
  }

  const score = data?.readiness_score || 0;

  return (
    <AppShell>
      <div className="max-w-6xl mx-auto px-6 py-10" data-testid="dashboard-page">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 mb-8 flex-wrap">
          <div>
            <p className="text-[11px] uppercase tracking-[0.2em] text-yellow-300">Dashboard</p>
            <h1 className="font-display text-4xl md:text-5xl mt-2 text-white">
              Hey, <span className="gradient-heading">{user?.name?.split(" ")[0] || "Student"}</span> 👋
            </h1>
            <p className="text-slate-400 mt-2">Here&apos;s your career readiness snapshot.</p>
          </div>
          <div className="brimi-card px-6 py-5 min-w-[220px]" data-testid="readiness-score-card">
            <p className="text-[11px] uppercase tracking-[0.2em] text-slate-500">Career Readiness</p>
            <div className="flex items-end gap-2 mt-1">
              <p className="font-display text-5xl text-slate-900">{score}<span className="text-2xl text-slate-500">%</span></p>
              <TrendingUp className="w-5 h-5 text-mint-500 mb-2" style={{ color: "#34D399" }} />
            </div>
            <div className="h-2 bg-slate-100 rounded-full mt-3 overflow-hidden">
              <div className="h-full transition-all duration-500" style={{ width: `${score}%`, background: "linear-gradient(90deg, #FB7185, #FBBF24, #34D399)" }} />
            </div>
          </div>
        </div>

        {/* Progress checklist */}
        <div className="grid md:grid-cols-4 gap-4 mb-8">
          <StepCard done={data?.profile_complete} title="Set up profile" desc="Grade, subjects, scores." to="/onboarding" testid="step-profile" />
          <StepCard done={data?.quiz_complete} title="Take the quiz" desc="30-question RIASEC test." to="/quiz" testid="step-quiz" />
          <StepCard done={data?.roadmap_ready} title="Get your roadmap" desc="AI generated career plan." to="/roadmap" testid="step-roadmap" />
          <StepCard done={(data?.saved_careers_count + data?.saved_colleges_count) > 0} title="Save favorites" desc="Careers + colleges." to="/careers" testid="step-saved" />
        </div>

        {/* Achievement gallery */}
        <div className="mb-8">
          <AchievementsRow />
        </div>

        {/* RIASEC + Careers */}
        <div className="grid md:grid-cols-12 gap-6">
          <div className="md:col-span-7 brimi-card p-6" data-testid="riasec-card">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-[11px] uppercase tracking-[0.2em] text-slate-500">Your RIASEC profile</p>
                <h3 className="font-display text-2xl text-slate-900 mt-1">Interest breakdown</h3>
              </div>
              {!data?.quiz_complete && (
                <Link to="/quiz" data-testid="take-quiz-cta" className="brimi-pill accent-coral text-sm flex items-center gap-2">
                  Take the quiz <ArrowRight className="w-4 h-4" />
                </Link>
              )}
            </div>
            {data?.quiz_scores ? (
              <div className="space-y-3">
                {Object.entries(data.quiz_scores).map(([code, val]) => (
                  <div key={code}>
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="font-bold text-slate-800">{code} — {codeName(code)}</span>
                      <span className="text-slate-500">{val}%</span>
                    </div>
                    <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full transition-all" style={{ width: `${val}%`, background: RIASEC_COLORS[code] }} />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500">
                <ClipboardList className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                Take the interest quiz to see your profile.
              </div>
            )}
          </div>

          <div className="md:col-span-5 brimi-card p-6" data-testid="suggested-careers-card">
            <p className="text-[11px] uppercase tracking-[0.2em] text-slate-500">Suggested careers</p>
            <h3 className="font-display text-2xl text-slate-900 mt-1">Match your vibe</h3>
            <div className="mt-4 space-y-3">
              {(data?.suggested_careers || []).length === 0 && (
                <p className="text-sm text-slate-500">Take the quiz to unlock personalized suggestions.</p>
              )}
              {(data?.suggested_careers || []).map((c) => (
                <Link key={c.id} to={`/careers?open=${c.id}`} data-testid={`sug-career-${c.id}`}
                  className="flex items-center justify-between p-3 rounded-xl border border-slate-200 hover:border-slate-300 hover:bg-slate-50 transition">
                  <div>
                    <p className="font-bold text-slate-900 text-sm">{c.name}</p>
                    <p className="text-xs text-slate-500">Matched via <b style={{ color: RIASEC_COLORS[c.match_code] }}>{c.match_code}</b> — {codeName(c.match_code)}</p>
                  </div>
                  <ArrowRight className="w-4 h-4 text-slate-400" />
                </Link>
              ))}
            </div>
          </div>
        </div>

        {/* Quick actions */}
        <div className="grid md:grid-cols-4 gap-4 mt-8">
          <QuickAction to="/roadmap" icon={Map} label="My Roadmap" desc="AI-generated plan" color="#FBBF24" testid="qa-roadmap" />
          <QuickAction to="/chat" icon={MessageSquare} label="AI Coach" desc="Ask anything" color="#FB7185" testid="qa-chat" />
          <QuickAction to="/careers" icon={Compass} label="Explore Careers" desc="15+ fields" color="#34D399" testid="qa-careers" />
          <QuickAction to="/colleges" icon={GraduationCap} label="Explore Colleges" desc="India + Global" color="#3B82F6" testid="qa-colleges" />
        </div>
      </div>
    </AppShell>
  );
}

function codeName(c) {
  return {
    R: "Realistic", I: "Investigative", A: "Artistic", S: "Social", E: "Enterprising", C: "Conventional",
  }[c];
}

function StepCard({ done, title, desc, to, testid }) {
  return (
    <Link to={to} data-testid={testid} className={`brimi-card p-5 block hover:no-underline`}>
      <div className="flex items-center gap-2">
        <div className={`w-6 h-6 rounded-full flex items-center justify-center ${done ? "" : "border-2 border-slate-300"}`} style={done ? { background: "#34D399" } : {}}>
          {done && <svg className="w-4 h-4 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round" /></svg>}
        </div>
        <p className="font-bold text-slate-900 text-sm">{title}</p>
      </div>
      <p className="text-xs text-slate-500 mt-2">{desc}</p>
    </Link>
  );
}

function QuickAction({ to, icon: Icon, label, desc, color, testid }) {
  return (
    <Link to={to} data-testid={testid} className="brimi-card p-5 flex items-start gap-3 hover:no-underline">
      <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: color + "22", color }}>
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <p className="font-bold text-slate-900">{label}</p>
        <p className="text-xs text-slate-500">{desc}</p>
      </div>
    </Link>
  );
}
